/**
 * SSSS Algorithm detection and key derivation utilities
 *
 * Handles:
 * - Version detection (v1 vs v2)
 * - Key derivation functions (PBKDF2, AES)
 * - Algorithm validation
 * - KDF parameter extraction
 */

import logger from '../../utils/logger';

// Algorithm constants
export const Algorithms = {
  AES_HMAC_SHA2: 'm.secret_storage.v1.aes-hmac-sha2',
  PBKDF2: 'm.pbkdf2',
};

// Default parameters
const DEFAULT_PBKDF2_ITERATIONS = 500000;
const DEFAULT_PBKDF2_BITS = 256;

// gematik A_25616 / OWASP PBKDF2 minimum iteration floor (SHA-512)
export const MIN_PBKDF2_ITERATIONS = 210000;

/**
 * Detect SSSS version from key info
 * @param {Object} keyInfo - The key info object from account data
 * @returns {Object} Version info with version string and details
 */
export function detectSSSSVersion(keyInfo) {
  if (!keyInfo) {
    return {
      version: 'unknown',
      algorithm: null,
      hasPassphrase: false,
      supported: false,
    };
  }

  const algorithm = keyInfo.algorithm;
  const hasPassphrase = !!keyInfo.passphrase;

  // v2: Uses m.secret_storage.v1.aes-hmac-sha2
  if (algorithm === Algorithms.AES_HMAC_SHA2) {
    return {
      version: 'v2',
      algorithm,
      hasPassphrase,
      supported: true,
      details: {
        mac: keyInfo.mac,
        iv: keyInfo.iv,
        passphrase: keyInfo.passphrase,
      },
    };
  }

  // v1: Older format or passphrase-based
  if (hasPassphrase && (!algorithm || algorithm === Algorithms.PBKDF2)) {
    return {
      version: 'v1',
      algorithm: Algorithms.PBKDF2,
      hasPassphrase: true,
      supported: true,
      details: {
        passphrase: keyInfo.passphrase,
      },
    };
  }

  // Unknown or unsupported
  logger.warn(`Unsupported SSSS algorithm: ${algorithm}`);
  return {
    version: 'unknown',
    algorithm,
    hasPassphrase,
    supported: false,
  };
}

/**
 * Validate if an algorithm is supported
 * @param {string} algorithm - Algorithm identifier
 * @returns {boolean} True if supported
 */
export function validateAlgorithm(algorithm) {
  return algorithm === Algorithms.AES_HMAC_SHA2 || algorithm === Algorithms.PBKDF2;
}

/**
 * Get KDF parameters from key info
 * @param {Object} keyInfo - Key info object
 * @returns {Object|null} KDF parameters or null
 */
export function getKDFParams(keyInfo) {
  if (!keyInfo) {
    return null;
  }

  // Check for passphrase-based KDF
  if (keyInfo.passphrase) {
    const passphrase = keyInfo.passphrase;
    return {
      type: 'passphrase',
      algorithm: passphrase.algorithm || Algorithms.PBKDF2,
      salt: passphrase.salt,
      iterations: passphrase.iterations || DEFAULT_PBKDF2_ITERATIONS,
      bits: passphrase.bits || DEFAULT_PBKDF2_BITS,
    };
  }

  // Direct key usage (no KDF)
  if (keyInfo.algorithm === Algorithms.AES_HMAC_SHA2 && !keyInfo.passphrase) {
    return {
      type: 'direct',
      algorithm: keyInfo.algorithm,
    };
  }

  return null;
}

/**
 * Derive key using PBKDF2
 * @param {Uint8Array} keyBytes - Input key material
 * @param {Object} passphraseInfo - Passphrase parameters
 * @param {Object} [options] - Behaviour options
 * @param {boolean} [options.strict=true] - When true, sub-threshold iterations
 *   throw a typed error (write paths). When false, they log + resolve to null
 *   so existing graceful unlock-failure handling can route the user to a
 *   re-bootstrap prompt (read paths).
 * @returns {Promise<Uint8Array|null>} Derived key, or null when strict=false
 *   and iterations are below A_25616 minimum
 */
export async function deriveKeyPBKDF2(keyBytes, passphraseInfo, { strict = true } = {}) {
  const {
    algorithm = Algorithms.PBKDF2,
    salt,
    iterations = DEFAULT_PBKDF2_ITERATIONS,
    bits = DEFAULT_PBKDF2_BITS,
  } = passphraseInfo;

  if (algorithm !== Algorithms.PBKDF2 && algorithm !== 'm.pbkdf2') {
    throw new Error(`Unsupported passphrase algorithm: ${algorithm}`);
  }

  if (!salt) {
    throw new Error('Salt is required for PBKDF2');
  }

  if (!Number.isInteger(iterations) || iterations < MIN_PBKDF2_ITERATIONS) {
    const detail = `PBKDF2 iterations (${iterations}) below A_25616 minimum (${MIN_PBKDF2_ITERATIONS})`;
    if (!strict) {
      logger.warn(`A_25616: ${detail}; refusing to derive (read path)`);
      return null;
    }
    const err = new Error(detail);
    err.code = 'WEAK_KDF_PARAMS';
    throw err;
  }

  try {
    // Import key material
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      keyBytes,
      'PBKDF2',
      false,
      ['deriveBits'],
    );

    // Derive bits
    const derivedBits = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: typeof salt === 'string' ? new TextEncoder().encode(salt) : salt,
        iterations,
        hash: 'SHA-512',
      },
      keyMaterial,
      bits,
    );

    return new Uint8Array(derivedBits);
  } catch (error) {
    logger.error('PBKDF2 derivation failed:', error.message);
    throw new Error('Failed to derive key using PBKDF2');
  }
}

/**
 * Derive key for v1 SSSS
 * @param {Uint8Array} keyBytes - Recovery key bytes
 * @param {Object} keyInfo - Key info from SSSS
 * @returns {Promise<Uint8Array|null>} Derived key, or null when the
 *   server-supplied PBKDF2 iterations fall below the A_25616 floor
 *   (read-path uses non-strict deriveKeyPBKDF2).
 */
export async function deriveKeyV1(keyBytes, keyInfo) {
  if (keyInfo.passphrase) {
    return deriveKeyPBKDF2(keyBytes, keyInfo.passphrase, { strict: false });
  }

  // Direct usage for v1 without passphrase
  return keyBytes;
}

/**
 * Derive key for v2 SSSS
 * @param {Uint8Array} keyBytes - Recovery key bytes
 * @param {Object} keyInfo - Key info from SSSS
 * @returns {Promise<Uint8Array|null>} Derived key, or null when the
 *   server-supplied PBKDF2 iterations fall below the A_25616 floor
 *   (read-path uses non-strict deriveKeyPBKDF2).
 */
export async function deriveKeyV2(keyBytes, keyInfo) {
  // v2 with passphrase
  if (keyInfo.passphrase) {
    return deriveKeyPBKDF2(keyBytes, keyInfo.passphrase, { strict: false });
  }

  // v2 direct usage (most common)
  if (keyInfo.algorithm === Algorithms.AES_HMAC_SHA2) {
    logger.debug('RECOVERY deriveKeyV2: AES-HMAC-SHA2, key length:', keyBytes.length);
    // For AES-HMAC-SHA2 without passphrase, the recovery key is used directly
    // The SDK/server will handle the key expansion internally if needed
    logger.debug('RECOVERY deriveKeyV2: Using key directly for AES-HMAC-SHA2');
    return keyBytes;
  }

  throw new Error(`Unsupported v2 configuration: ${keyInfo.algorithm}`);
}

/**
 * Derive encryption key based on detected version
 * @param {Uint8Array} keyBytes - Recovery key bytes
 * @param {Object} keyInfo - Key info from SSSS
 * @returns {Promise<Uint8Array|null>} Derived key ready for use, or null
 *   when sub-A_25616 PBKDF2 parameters were detected on the read path.
 *   Callers must treat null as a typed failure (see SSSSErrors.WEAK_KDF_PARAMS).
 */
export async function deriveSSSSKey(keyBytes, keyInfo) {
  const versionInfo = detectSSSSVersion(keyInfo);

  if (!versionInfo.supported) {
    throw new Error(`Unsupported SSSS version: ${versionInfo.version}`);
  }

  logger.debug(`Deriving key for SSSS ${versionInfo.version}`, {
    algorithm: versionInfo.algorithm,
    hasPassphrase: versionInfo.hasPassphrase,
  });

  switch (versionInfo.version) {
    case 'v1':
      return deriveKeyV1(keyBytes, keyInfo);
    case 'v2':
      return deriveKeyV2(keyBytes, keyInfo);
    default:
      throw new Error(`Cannot derive key for version: ${versionInfo.version}`);
  }
}

/**
 * Create key info for new SSSS setup
 * @param {string} version - Version to create ('v1' or 'v2')
 * @param {Object} options - Optional parameters
 * @returns {Object} Key info object
 */
export function createKeyInfo(version = 'v2', options = {}) {
  if (version === 'v2') {
    const keyInfo = {
      algorithm: Algorithms.AES_HMAC_SHA2,
    };

    // Add passphrase if requested
    if (options.usePassphrase) {
      keyInfo.passphrase = {
        algorithm: Algorithms.PBKDF2,
        salt: options.salt || generateSalt(),
        iterations: options.iterations || DEFAULT_PBKDF2_ITERATIONS,
        bits: options.bits || DEFAULT_PBKDF2_BITS,
      };
    }

    // Add MAC and IV for validation
    if (options.mac) {
      keyInfo.mac = options.mac;
    }
    if (options.iv) {
      keyInfo.iv = options.iv;
    }

    return keyInfo;
  }

  if (version === 'v1') {
    const keyInfo = {};

    // v1 typically uses passphrase
    if (options.usePassphrase !== false) {
      keyInfo.passphrase = {
        algorithm: Algorithms.PBKDF2,
        salt: options.salt || generateSalt(),
        iterations: options.iterations || DEFAULT_PBKDF2_ITERATIONS,
        bits: options.bits || DEFAULT_PBKDF2_BITS,
      };
    }

    return keyInfo;
  }

  throw new Error(`Unsupported version for key info creation: ${version}`);
}

/**
 * Generate a random salt for PBKDF2
 * @returns {string} Base64-encoded salt
 */
function generateSalt() {
  const saltBytes = new Uint8Array(32);
  crypto.getRandomValues(saltBytes);
  return btoa(String.fromCharCode(...saltBytes));
}

/**
 * Derive AES and HMAC keys from a master key using HKDF
 * This follows the Matrix SSSS specification for key derivation
 * @param {Uint8Array} key - The master key (32 bytes)
 * @param {string} name - The name for key derivation (empty string for verification)
 * @returns {Promise<[CryptoKey, CryptoKey]>} [aesKey, hmacKey]
 */
export async function deriveKeys(key, name) {
  // Salt for HKDF with 8 bytes of zeros (Matrix spec)
  const zeroSalt = new Uint8Array(8);

  // Import the master key for HKDF
  const hkdfKey = await crypto.subtle.importKey(
    'raw',
    key,
    { name: 'HKDF' },
    false,
    ['deriveBits'],
  );

  // Derive 512 bits (64 bytes) using HKDF
  const keyBits = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      salt: zeroSalt,
      info: new TextEncoder().encode(name),
      hash: 'SHA-256',
    },
    hkdfKey,
    512, // 512 bits = 64 bytes
  );

  const keyBytes = new Uint8Array(keyBits);
  const aesKeyBytes = keyBytes.slice(0, 32);
  const hmacKeyBytes = keyBytes.slice(32);

  // Import AES key
  const aesKey = await crypto.subtle.importKey(
    'raw',
    aesKeyBytes,
    { name: 'AES-CTR' },
    false,
    ['encrypt', 'decrypt'],
  );

  // Import HMAC key
  const hmacKey = await crypto.subtle.importKey(
    'raw',
    hmacKeyBytes,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign', 'verify'],
  );

  return [aesKey, hmacKey];
}

/**
 * Verify MAC for key validation
 * @param {Uint8Array} keyBytes - The key to verify (32 bytes)
 * @param {Object} keyInfo - Key info containing MAC and IV
 * @returns {Promise<boolean>} True if MAC is valid
 */
export async function verifyKeyMAC(keyBytes, keyInfo) {
  if (!keyInfo.mac || !keyInfo.iv) {
    logger.debug('RECOVERY No MAC/IV for verification');
    return true; // No MAC to verify
  }

  try {
    // Validate input key
    if (!keyBytes || keyBytes.length !== 32) {
      logger.error('RECOVERY Invalid key length for MAC verification:', keyBytes?.length || 0, 'expected 32');
      return false;
    }

    // Derive AES and HMAC keys using HKDF with empty name for verification
    logger.debug('RECOVERY Deriving keys using HKDF for MAC verification');
    const [aesKey, hmacKey] = await deriveKeys(keyBytes, '');

    // Decode IV from base64
    const iv = Uint8Array.from(atob(keyInfo.iv), c => c.charCodeAt(0));

    // For MAC verification, encrypt 32 bytes of zeros (Matrix spec)
    const zeroData = new Uint8Array(32);

    // Encrypt the zero data using AES-CTR
    const ciphertext = await crypto.subtle.encrypt(
      {
        // snyk:ignore:Use of Cipher Without Integrity Protection
        name: 'AES-CTR',
        counter: iv,
        length: 64, // Counter length in bits
      },
      aesKey,
      zeroData,
    );

    // Calculate HMAC of the ciphertext
    const calculatedMac = await crypto.subtle.sign(
      { name: 'HMAC' },
      hmacKey,
      ciphertext,
    );

    // Decode expected MAC from base64
    const expectedMac = Uint8Array.from(atob(keyInfo.mac), c => c.charCodeAt(0));

    // Compare MACs
    const calculatedMacArray = new Uint8Array(calculatedMac);
    if (expectedMac.length !== calculatedMacArray.length) {
      logger.debug('RECOVERY MAC length mismatch');
      return false;
    }

    let isValid = true;
    for (let i = 0; i < expectedMac.length; i++) {
      if (expectedMac[i] !== calculatedMacArray[i]) {
        isValid = false;
        break;
      }
    }

    logger.debug('RECOVERY MAC verification result:', isValid);
    return isValid;

  } catch (error) {
    logger.error('RECOVERY MAC verification failed:', error.message);
    return false;
  }
}

/**
 * Extract human-readable error from derivation failure
 * @param {Error} error - The error from derivation
 * @returns {Object} Error info with code and message
 */
export function mapDerivationError(error) {
  const message = error.message?.toLowerCase() || '';

  if (message.includes('unsupported')) {
    return {
      code: 'UNSUPPORTED_ALGORITHM',
      message: 'This secret storage version is not supported',
    };
  }

  if (message.includes('salt')) {
    return {
      code: 'INVALID_SALT',
      message: 'Invalid or missing salt for key derivation',
    };
  }

  if (message.includes('pbkdf2')) {
    return {
      code: 'PBKDF2_FAILED',
      message: 'Failed to derive key using PBKDF2',
    };
  }

  if (message.includes('iterations')) {
    return {
      code: 'INVALID_ITERATIONS',
      message: 'Invalid iteration count for key derivation',
    };
  }

  return {
    code: 'DERIVATION_FAILED',
    message: 'Failed to derive encryption key from recovery key',
  };
}